import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { useToastr } from '@farmshare/ui-components';
import { environment } from 'environments/environment';
import {
  useCustomerSavedCardsQuery,
  useDepositSetupIntentCreateMutation,
} from '../../../lib/graphql';

/** Deposit collection methods — mirror the backend `EDepositMethod`. */
export const DEPOSIT_METHOD = {
  None: 'none',
  CardOnFile: 'card_on_file',
  Notification: 'notification',
} as const;

export type DepositMethod =
  (typeof DEPOSIT_METHOD)[keyof typeof DEPOSIT_METHOD];

export interface DepositCollectionValue {
  method: DepositMethod;
  /** Required when `method` is `card_on_file`. */
  paymentMethodId?: string;
}

interface Props {
  vendorId: string;
  producerId: string;
  /** The vendor's connected Stripe account id (from settings.vendor.stripe_config.id). */
  connectedAccountId?: string | null;
  headCount: number;
  /** Per-head deposit amount from processor settings. */
  schedulingDeposit: number;
  value: DepositCollectionValue;
  onChange: (value: DepositCollectionValue) => void;
  /**
   * Whether "charge a card on file" is on offer. Default true.
   *
   * False when the customer does not exist yet — a wild-game walk-in taking a
   * brand-new hunter's details at the counter has no account to hold a card,
   * so the option could only ever fail. Offering a choice that cannot work is
   * worse than not offering it, especially with a queue behind them.
   */
  allowCardOnFile?: boolean;
}

const formatUsd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const cardLabel = (c: {
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
}) => {
  const brand = c.brand ? c.brand[0].toUpperCase() + c.brand.slice(1) : 'Card';
  const exp =
    c.expMonth && c.expYear
      ? ` · exp ${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}`
      : '';
  return `${brand} •••• ${c.last4 ?? '????'}${exp}`;
};

function AddCardForm({
  onAdded,
  onClose,
}: {
  onAdded: (paymentMethodId: string) => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { push } = useToastr();
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        push({
          title: 'Card not saved',
          body: error.message ?? 'Please check the card details.',
          severity: 'error',
        });
        return;
      }
      const pmId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;
      if (!pmId) {
        push({
          title: 'Card not saved',
          body: 'No payment method was returned.',
          severity: 'error',
        });
        return;
      }
      onAdded(pmId);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PaymentElement />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        By saving, you confirm the customer authorized storing this card for
        deposit and future charges.
      </Typography>
      <DialogActions sx={{ px: 0, mt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!stripe || submitting}
        >
          {submitting ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </>
  );
}

interface AddCardDialogProps {
  open: boolean;
  onClose: () => void;
  stripePromise: Promise<Stripe | null>;
  vendorId: string;
  producerId: string;
  onAdded: (paymentMethodId: string) => void;
}

function AddCardDialog({
  open,
  onClose,
  stripePromise,
  vendorId,
  producerId,
  onAdded,
}: AddCardDialogProps) {
  const { push } = useToastr();
  const [createSetupIntent, { data, loading }] =
    useDepositSetupIntentCreateMutation();

  // Lazily create the SetupIntent once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    createSetupIntent({ variables: { vendorId, userId: producerId } }).catch(
      (e) =>
        push({
          title: 'Could not start card entry',
          body: (e as Error).message,
          severity: 'error',
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clientSecret = data?.depositSetupIntentCreate?.clientSecret;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a card</DialogTitle>
      <DialogContent dividers>
        {loading || !clientSecret ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <AddCardForm onAdded={onAdded} onClose={onClose} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Deposit collection options shown to a processor booking on a customer's
 * behalf when the processor has deposits + Stripe enabled. Lets them skip the
 * deposit, charge a saved/added card on file, or send the customer a link to
 * pay. Renders nothing when there's no deposit or no connected Stripe account.
 * @param root0
 * @param root0.vendorId
 * @param root0.producerId
 * @param root0.connectedAccountId
 * @param root0.headCount
 * @param root0.schedulingDeposit
 * @param root0.value
 * @param root0.onChange
 */
export function DepositCollectionSection({
  vendorId,
  producerId,
  connectedAccountId = undefined,
  headCount,
  schedulingDeposit,
  value,
  onChange,
  allowCardOnFile = true,
}: Props) {
  const [addOpen, setAddOpen] = useState(false);

  const total = schedulingDeposit * Math.max(headCount, 0);

  const { data, loading, refetch } = useCustomerSavedCardsQuery({
    variables: { vendorId, userId: producerId },
    skip: !vendorId || !producerId,
    fetchPolicy: 'cache-and-network',
  });
  const cards = useMemo(
    () => (data?.customerSavedCards ?? []).filter(Boolean),
    [data?.customerSavedCards],
  );

  // A connected-account-scoped Stripe instance is required so Elements can
  // confirm a SetupIntent created on that account.
  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () =>
      connectedAccountId
        ? loadStripe(environment.stripeKey, {
            stripeAccount: connectedAccountId,
          })
        : null,
    [connectedAccountId],
  );

  if (!schedulingDeposit || schedulingDeposit <= 0 || !connectedAccountId) {
    return null;
  }

  const setMethod = (method: DepositMethod) => {
    if (method === DEPOSIT_METHOD.CardOnFile) {
      onChange({ method, paymentMethodId: cards[0]?.id });
    } else {
      onChange({ method });
    }
  };

  const onCardAdded = async (paymentMethodId: string) => {
    setAddOpen(false);
    await refetch();
    onChange({ method: DEPOSIT_METHOD.CardOnFile, paymentMethodId });
  };

  let cardOnFileContent: ReactNode;
  if (loading && cards.length === 0) {
    cardOnFileContent = (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading saved cards…
        </Typography>
      </Stack>
    );
  } else if (cards.length > 0) {
    cardOnFileContent = (
      <TextField
        select
        size="small"
        label="Card on file"
        value={value.paymentMethodId ?? ''}
        onChange={(e) =>
          onChange({
            method: DEPOSIT_METHOD.CardOnFile,
            paymentMethodId: e.target.value,
          })
        }
        sx={{ maxWidth: 360 }}
        // Card brand + last-4 ("Visa •••• 1234") is CC info we render
        // ourselves — mask both the selected value and each dropdown option
        // from Contentsquare replay. The menu portals out of this element, so
        // the MenuItems must be tagged individually too.
        data-cs-private
      >
        {cards.map((c) => (
          <MenuItem key={c!.id} value={c!.id} data-cs-private>
            {cardLabel(c!)}
          </MenuItem>
        ))}
      </TextField>
    );
  } else {
    cardOnFileContent = (
      <Typography variant="body2" color="text.secondary">
        No saved cards for this customer yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Deposit · {formatUsd(total)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {formatUsd(schedulingDeposit)} per head × {headCount} head
      </Typography>

      <FormControl sx={{ mt: 1 }}>
        <RadioGroup
          value={value.method}
          onChange={(e) => setMethod(e.target.value as DepositMethod)}
        >
          <FormControlLabel
            value={DEPOSIT_METHOD.None}
            control={<Radio size="small" />}
            label="Don't collect a deposit"
          />
          {allowCardOnFile && (
            <FormControlLabel
              value={DEPOSIT_METHOD.CardOnFile}
              control={<Radio size="small" />}
              label="Charge a card on file"
            />
          )}
          <FormControlLabel
            value={DEPOSIT_METHOD.Notification}
            control={<Radio size="small" />}
            label="Send the customer a deposit notification"
          />
        </RadioGroup>
      </FormControl>

      {value.method === DEPOSIT_METHOD.CardOnFile && (
        <Stack spacing={1} sx={{ mt: 1, pl: 4 }}>
          {cardOnFileContent}
          <Box>
            <Button size="small" onClick={() => setAddOpen(true)}>
              + Add a card
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            The card will be charged {formatUsd(total)} now and saved for future
            bookings.
          </Typography>
        </Stack>
      )}

      {value.method === DEPOSIT_METHOD.Notification && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
          We&apos;ll email/text the customer a link to pay their{' '}
          {formatUsd(total)} deposit.
        </Typography>
      )}

      {addOpen && stripePromise && (
        <AddCardDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          stripePromise={stripePromise}
          vendorId={vendorId}
          producerId={producerId}
          onAdded={onCardAdded}
        />
      )}
    </Box>
  );
}
